/* eslint-disable object-shorthand */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { overlayEvents } from "overlays/OverlayEvents";
import FlyoutCss from "./Flyout.css";
import FlyoutTabItem from "./FlyoutTabItem";
import { flyoutHideManager } from "utils/ComponentHideManager";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import * as codes from "dc-preview-utils/src/Constants/key-codes";
import { focusSelectedChild, focusPreviousChild, focusNextChild } from "dc-preview-utils/src/Utils/FocusUtils";
import { INTERACTIVE_ARIA_ROLES } from "constants/constants";
import { closeFlyoutReasons } from "constants/enums";
import { shouldSelectVerbOnClick } from "utils/QuickToolsUtils";
import { TOOLTIP } from "dc-preview-utils/src/Constants/constants";
import { cancellablePromise } from "utils/CancellablePromise";

const Flyout = props => {
  const {
    verbDetails,
    setActiveVerb,
    selectedVerb,
    allowRepeatedSelection,
    closeFlyout,
    component: Component,
    shouldAutoCloseFlyout,
    cursorType,
    autoHideTime,
    forceShowFlyout,
    delayTimeOnSelection,
    initialAutoHide,
    shouldShowConditionalToolTip
  } = props;

  const delayedFlyoutPromise = useRef(null);
  const cancelDelayedPromise = useCallback(() => {
    if (delayedFlyoutPromise.current) {
      delayedFlyoutPromise.current.cancel();
      delayedFlyoutPromise.current = null;
    }
  }, []);

  const [shouldAutoHide, setShouldAutoHide] = useState(false);
  const [isMouseOverFlyout, setIsMouseOverFlyout] = useState(false);
  const [isFlyoutInFocus, setIsFlyoutInFocus] = useState(false);

  const elementHandle = useRef(null);

  const verbClickHandler = (verbId, event) => {
    if (verbId && (allowRepeatedSelection || selectedVerb !== verbId)) {
      setActiveVerb(verbId, { shouldSelectVerbOnClick: shouldSelectVerbOnClick(verbId) });
    }

    // Cancel old delayed promise
    cancelDelayedPromise();
    const resolvedPromiseAfterDelay = new Promise(resolve => {
      setTimeout(resolve, delayTimeOnSelection);
    });
    delayedFlyoutPromise.current = cancellablePromise(resolvedPromiseAfterDelay);
    delayedFlyoutPromise.current.promise.then(() => {
      closeFlyout();
      cancelDelayedPromise();
    });
    event && event.stopPropagation();
  };

  const handleKeyMovement = event => {
    if (event.keyCode === codes.ESCAPE) {
      closeFlyout();
      event.stopPropagation();
    } else if (event.type === "keydown") {
      // Use arrow keys to navigate within the flyout tabs
      if (event.keyCode === codes.ARROW_DOWN) {
        focusNextChild(elementHandle.current, INTERACTIVE_ARIA_ROLES, event);
      } else if (event.keyCode === codes.ARROW_UP) {
        focusPreviousChild(elementHandle.current, INTERACTIVE_ARIA_ROLES, event);
      } else if (event.keyCode === codes.TAB) {
        event.preventDefault();
      }

      if (elementHandle && elementHandle.current) {
        setIsFlyoutInFocus(elementHandle.current.contains(document.activeElement));
      }
    }
  };

  const handleBlur = reason => {
    closeFlyout(reason);
  };

  const handleWindowClick = () => {
    handleBlur();
  };

  const handlePageClick = () => {
    handleBlur(closeFlyoutReasons.PAGE_CLICK);
  };

  const handleMouseMove = event => {
    elementHandle && elementHandle.current ? flyoutHideManager.handleMouseMove(elementHandle.current, event) : null;
  };

  const setShouldAutoHideHelper = __shouldAutoHide__ => {
    setShouldAutoHide(__shouldAutoHide__);
  };

  const setIsMouseOverFlyoutHelper = __isMouseOverFlyout__ => {
    setIsMouseOverFlyout(__isMouseOverFlyout__);
  };

  useEffect(() => {
    let _removeMouseMoveHandler;

    if (shouldAutoCloseFlyout) {
      flyoutHideManager.init({
        setShouldAutoHide: setShouldAutoHideHelper,
        setIsMouseOverFlyout: setIsMouseOverFlyoutHelper,
        hideTimeout: autoHideTime,
        shouldAutoHideInitial: initialAutoHide
      });
      _removeMouseMoveHandler = addEventListener(window, "mousemove", handleMouseMove);
    }

    const _removeKeydownHandler = addEventListener(elementHandle.current, "keydown", handleKeyMovement);
    const _removeClickHandler = addEventListener(window, "click", handleWindowClick);
    overlayEvents.on("pageclick", handlePageClick);
    if (!shouldShowConditionalToolTip) {
      focusSelectedChild(elementHandle.current, INTERACTIVE_ARIA_ROLES);
    }
    return () => {
      if (shouldAutoCloseFlyout) {
        _removeMouseMoveHandler && _removeMouseMoveHandler();
      }
      _removeKeydownHandler && _removeKeydownHandler();
      _removeClickHandler && _removeClickHandler();
      overlayEvents.unregister("pageclick", handlePageClick);
      cancelDelayedPromise();
    };
  }, []);

  useEffect(() => {
    if (!isMouseOverFlyout && !isFlyoutInFocus && shouldAutoHide && !forceShowFlyout) {
      closeFlyout();
    }
  }, [shouldAutoHide, isMouseOverFlyout, isFlyoutInFocus, forceShowFlyout]);

  return (
    <div ref={elementHandle} data-testid="FlyOut.Menu">
      {Component === null ? (
        <div className={FlyoutCss.FlyoutTabList} role="tablist">
          {verbDetails.map(verb =>
            verb.icon ? (
              <FlyoutTabItem
                icon={verb.icon}
                verbClickHandler={event => verbClickHandler(verb.verbId, event)}
                label={verb.label}
                isSelected={selectedVerb === verb.verbId}
                onKeyPress={e => (e.which === codes.ENTER ? verbClickHandler(verb.verbId, e) : null)}
                cursorType={selectedVerb === verb.verbId && cursorType}
                data-test-id={`flyout-${verb.label}`}
                tooltip={{
                  text: verb.conditionalTooltip || verb.tooltip,
                  variant: verb.tooltipVariant,
                  placement: TOOLTIP.PLACEMENT.RIGHT,
                  show: verb.isControlled
                }}
              />
            ) : null
          )}
        </div>
      ) : (
        <div className={FlyoutCss.ComponentWrapper}>
          <Component />
        </div>
      )}
    </div>
  );
};

Flyout.propTypes = {
  verbDetails: PropTypes.arrayOf(PropTypes.object),
  setActiveVerb: PropTypes.func.isRequired,
  closeFlyout: PropTypes.func,
  selectedVerb: PropTypes.string,
  component: PropTypes.object,
  allowRepeatedSelection: PropTypes.bool,
  shouldAutoCloseFlyout: PropTypes.bool,
  cursorType: PropTypes.string,
  autoHideTime: PropTypes.number,
  forceShowFlyout: PropTypes.bool,
  delayTimeOnSelection: PropTypes.number,
  initialAutoHide: PropTypes.bool,
  shouldShowConditionalToolTip: PropTypes.bool
};

Flyout.defaultProps = {
  verbDetails: [],
  selectedVerb: null,
  component: null,
  allowRepeatedSelection: false,
  shouldAutoCloseFlyout: true,
  closeFlyout: () => {},
  cursorType: "deafult",
  autoHideTime: null,
  forceShowFlyout: false,
  delayTimeOnSelection: 0,
  initialAutoHide: true,
  shouldShowConditionalToolTip: false
};

export { Flyout };
